import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
  Chip,
  TextField,
  Grid,
  InputAdornment,
  Button,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  CircularProgress,
  FormControl
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { FormikProvider, useFormik } from 'formik';
import { axios } from 'utils/axios';
import { WasteItemDetail } from '../../@types/transactions';

type WasteItemsTabProps = {
  items: WasteItemDetail[];
  transactionNetWeight?: number;
};

// R&D Code mapping - based on common waste codes
const RANDD_CODES = [
  { code: 'R01', label: 'R1 - Use principally as a fuel or other means to generate energy' },
  { code: 'R02', label: 'R2 - Solvent reclamation/regeneration' },
  { code: 'R03', label: 'R3 - Recycling/reclamation of organic substances' },
  { code: 'R04', label: 'R4 - Recycling/reclamation of metals and metal compounds' },
  { code: 'R05', label: 'R5 - Recycling/reclamation of other inorganic materials' },
  { code: 'R06', label: 'R6 - Regeneration of acids or bases' },
  { code: 'R07', label: 'R7 - Recovery of components used for pollution abatement' },
  { code: 'R08', label: 'R8 - Recovery of components from catalysts' },
  { code: 'R09', label: 'R9 - Oil re-refining or other reuses of oil' },
  {
    code: 'R10',
    label: 'R10 - Land treatment resulting in benefit to agriculture or ecological improvement'
  },
  {
    code: 'R11',
    label: 'R11 - Use of wastes obtained from any of the operations numbered R01-R10'
  },
  {
    code: 'R12',
    label: 'R12 - Exchange of wastes for submission to any of the operations numbered R01-R11'
  },
  { code: 'R13', label: 'R13 - Storage of wastes pending any of the operations numbered R01-R12' }
];

const WasteItemsTab: React.FC<WasteItemsTabProps> = ({ items, transactionNetWeight = 0 }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [itemDetails, setItemDetails] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  // Reset selectedIndex if it's out of bounds or items array is empty
  useEffect(() => {
    if (items.length === 0 || selectedIndex >= items.length) {
      setSelectedIndex(0);
    }
  }, [items, selectedIndex]);

  const selected = items.length > 0 && selectedIndex < items.length ? items[selectedIndex] : null;

  // Fetch item details when selection changes
  useEffect(() => {
    if (selected?.id) {
      fetchItemDetails(selected.id);
    }
  }, [selected?.id]);

  const fetchItemDetails = async (itemId: string) => {
    setLoading(true);
    try {
      const response = await axios.get(`/rest/api/transaction-items/${itemId}`);
      if (response.data.success && response.data.data) {
        setItemDetails(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching item details:', error);
      enqueueSnackbar('Failed to load item details', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      Description: itemDetails?.Description || '',
      Containment: itemDetails?.Containment || '',
      ApproxQty: itemDetails?.ApproxQty || 0,
      SIC: itemDetails?.SIC || '',
      EWC: itemDetails?.EWC || '',
      HAZ: itemDetails?.HAZ || false,
      RANDD: itemDetails?.RANDD || '',
      Destination: itemDetails?.Destination || '',
      ProcessRoute: itemDetails?.ProcessRoute || '',
      ItemNetWeight: itemDetails?.ItemNetWeight || 0
    },
    onSubmit: async (values) => {
      if (!itemDetails?.Id) {
        enqueueSnackbar('Item ID not found', { variant: 'error' });
        return;
      }

      setSaving(true);
      try {
        // Prepare payload with all required fields from API
        // Convert numeric values to ensure proper type
        const payload = {
          ServiceItemId: itemDetails.ServiceItemId,
          GUID: itemDetails.GUID,
          HeaderID: itemDetails.HeaderID,
          SourceServiceJobId: itemDetails.SourceServiceJobId,
          ServiceRef: itemDetails.ServiceRef,
          SourceJobNo: itemDetails.SourceJobNo,
          SourceWasteItemID: itemDetails.SourceWasteItemID,
          ItemListNumber: itemDetails.ItemListNumber,
          Description: values.Description,
          Containment: values.Containment,
          ApproxQty: Number(values.ApproxQty) || 0,
          PhysicalForm: itemDetails.PhysicalForm,
          SIC: values.SIC,
          EWC: values.EWC,
          HAZ: Boolean(values.HAZ),
          HAZcodes: itemDetails.HAZcodes,
          RANDD: values.RANDD || null,
          WasteState: itemDetails.WasteState,
          CouncilArea: itemDetails.CouncilArea,
          WeightTicketId: itemDetails.WeightTicketId,
          WeightTicketStatus: itemDetails.WeightTicketStatus,
          CurrentStatus: itemDetails.CurrentStatus,
          Deleted: itemDetails.Deleted,
          AddedBy: itemDetails.AddedBy,
          AddedAt: itemDetails.AddedAt,
          UpdatedBy: itemDetails.UpdatedBy,
          UpdatedAt: itemDetails.UpdatedAt,
          SourcePopulate: itemDetails.SourcePopulate,
          Notes: itemDetails.Notes,
          Destination: values.Destination,
          StorageArea: itemDetails.StorageArea,
          ItemNetWeight: Number(values.ItemNetWeight) || 0,
          ProcessRoute: values.ProcessRoute,
          RecpTankBay: itemDetails.RecpTankBay,
          ConsignmentNote: itemDetails.ConsignmentNote,
          OriginSiteId: itemDetails.OriginSiteId,
          OriginSite: itemDetails.OriginSite,
          ProcessCost: itemDetails.ProcessCost,
          UnitCharge: itemDetails.UnitCharge,
          ServiceCharge: itemDetails.ServiceCharge,
          QtyProcessed: itemDetails.QtyProcessed,
          defaultCompanyId: itemDetails.defaultCompanyId,
          defaultCompanyName: itemDetails.defaultCompanyName
        };

        const response = await axios.put(`/rest/api/transaction-items/${itemDetails.Id}`, payload);
        if (response.data.success) {
          enqueueSnackbar('Item updated successfully', { variant: 'success' });
          // Refresh item details
          fetchItemDetails(itemDetails.Id.toString());
        }
      } catch (error: any) {
        console.error('Error updating item:', error);
        enqueueSnackbar(error.response?.data?.message || 'Failed to update item', {
          variant: 'error'
        });
      } finally {
        setSaving(false);
      }
    }
  });

  const selectedRANDD = RANDD_CODES.find((code) => code.code === formik.values.RANDD);

  return (
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
      <Card
        sx={{
          flex: { xs: 'none', md: '0 0 280px' },
          p: 2,
          maxHeight: 420,
          overflow: 'auto'
        }}
      >
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
          Waste Items ({items.length})
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <List disablePadding>
          {items.map((item, index) => (
            <ListItemButton
              key={item.id}
              selected={index === selectedIndex}
              onClick={() => setSelectedIndex(index)}
              sx={{
                mb: 1,
                borderRadius: 1,
                border: (theme: any) =>
                  index === selectedIndex
                    ? `1px solid ${theme.palette.primary.main}`
                    : '1px solid transparent'
              }}
            >
              <ListItemText
                primary={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="subtitle2">{item.title}</Typography>
                    {item.hazardous && (
                      <Chip label="HAZ" color="error" size="small" sx={{ fontWeight: 600 }} />
                    )}
                  </Stack>
                }
                secondary={
                  <Typography variant="caption" color="text.secondary">
                    {item.code} · {item.quantity} KG
                  </Typography>
                }
              />
            </ListItemButton>
          ))}
        </List>
      </Card>

      <Card sx={{ flex: 1, p: 3, minHeight: 420 }}>
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: 360
            }}
          >
            <CircularProgress />
          </Box>
        ) : selected && itemDetails ? (
          <FormikProvider value={formik}>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                formik.handleSubmit(e);
              }}
            >
              <Stack spacing={3}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Item Detail
                  </Typography>
                </Box>

                {/* Item Detail Section */}
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Description"
                      fullWidth
                      {...formik.getFieldProps('Description')}
                      error={Boolean(formik.touched.Description && formik.errors.Description)}
                      helperText={formik.touched.Description && formik.errors.Description}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      label="Approximate Qty"
                      fullWidth
                      type="number"
                      {...formik.getFieldProps('ApproxQty')}
                      error={Boolean(formik.touched.ApproxQty && formik.errors.ApproxQty)}
                      helperText={formik.touched.ApproxQty && formik.errors.ApproxQty}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      label="Containment"
                      fullWidth
                      {...formik.getFieldProps('Containment')}
                      error={Boolean(formik.touched.Containment && formik.errors.Containment)}
                      helperText={formik.touched.Containment && formik.errors.Containment}
                    />
                  </Grid>
                </Grid>

                {/* Classification Section */}
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                    Classification
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="SIC Code"
                        fullWidth
                        {...formik.getFieldProps('SIC')}
                        error={Boolean(formik.touched.SIC && formik.errors.SIC)}
                        helperText={formik.touched.SIC && formik.errors.SIC}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="EWC Code"
                        fullWidth
                        {...formik.getFieldProps('EWC')}
                        error={Boolean(formik.touched.EWC && formik.errors.EWC)}
                        helperText={formik.touched.EWC && formik.errors.EWC}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formik.values.HAZ}
                            onChange={(e) => formik.setFieldValue('HAZ', e.target.checked)}
                          />
                        }
                        label="Hazardous"
                      />
                    </Grid>
                  </Grid>
                </Box>

                {/* Processing Section */}
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                    Processing
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <Autocomplete
                          options={RANDD_CODES}
                          getOptionLabel={(option) => option.label}
                          value={selectedRANDD || null}
                          onChange={(_, newValue) => {
                            formik.setFieldValue('RANDD', newValue?.code || '');
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="R & D Code"
                              error={Boolean(formik.touched.RANDD && formik.errors.RANDD)}
                              helperText={formik.touched.RANDD && formik.errors.RANDD}
                            />
                          )}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Destination"
                        fullWidth
                        {...formik.getFieldProps('Destination')}
                        error={Boolean(formik.touched.Destination && formik.errors.Destination)}
                        helperText={formik.touched.Destination && formik.errors.Destination}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Process Route"
                        fullWidth
                        {...formik.getFieldProps('ProcessRoute')}
                        error={Boolean(formik.touched.ProcessRoute && formik.errors.ProcessRoute)}
                        helperText={formik.touched.ProcessRoute && formik.errors.ProcessRoute}
                      />
                    </Grid>
                  </Grid>
                </Box>

                {/* Weight Section */}
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                    Weight
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Item Net Weight (KG)"
                        fullWidth
                        type="number"
                        {...formik.getFieldProps('ItemNetWeight')}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">KG</InputAdornment>
                        }}
                        error={Boolean(formik.touched.ItemNetWeight && formik.errors.ItemNetWeight)}
                        helperText={formik.touched.ItemNetWeight && formik.errors.ItemNetWeight}
                      />
                    </Grid>
                  </Grid>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Transaction net weight: {transactionNetWeight} KG
                  </Typography>
                </Box>

                {/* Save Button */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <Button
                    type="button"
                    variant="contained"
                    color="primary"
                    disabled={saving}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      formik.handleSubmit();
                    }}
                    sx={{ minWidth: 150 }}
                  >
                    {saving ? <CircularProgress size={24} /> : 'Save Changes'}
                  </Button>
                </Box>
              </Stack>
            </form>
          </FormikProvider>
        ) : (
          <Box
            sx={{ height: 360, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <Typography variant="body2" color="text.secondary">
              Select a waste item to view details.
            </Typography>
          </Box>
        )}
      </Card>
    </Stack>
  );
};

export default WasteItemsTab;
